import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { tr } from '../lib/i18nHelpers';
import { useActiveOcrJobs } from '../lib/useActiveOcrJobs';
import { useDateFilter } from '../contexts/DateFilterContext';
import { ChevronDown, ChevronRight, CreditCard, Landmark } from 'lucide-react';

interface Props {
  endpoint: string;          // e.g. '/bank-statements/continuity' or '/card-statements/continuity'
  queryKey: string;          // e.g. 'bank-continuity' or 'card-continuity'
  type: 'bank' | 'card';    // affects labels and grouping display
  // When provided, month boxes become clickable and report the statement id
  // (e.g. opens the statement detail modal on the bank statements page).
  onSelectStatement?: (statementId: string) => void;
}

export default function ContinuityChain({ endpoint, queryKey, type, onSelectStatement }: Props) {
  const { i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  // Sidebar FY selector (2026-11-03): the coverage check scopes to the
  // selected financial year — only its months and warnings are shown. The
  // chain itself stays full-history (continuity is about the links).
  const { fyStartYear, startDate, endDate } = useDateFilter();
  const { data, isLoading } = useQuery({
    queryKey: [queryKey],
    queryFn: () => api(endpoint),
    staleTime: 0, // never serve stale — continuity must always reflect current DB state
  });

  // Nov 2 (2026-10-30): the continuity endpoint only counts reviewed
  // statements, so while OCR is still running the chain is PARTIAL and must
  // not read as an authoritative verdict — mirror the Workflow verification
  // cards' neutral "checking…" gate (2026-10-28 progress-honesty work).
  const { jobs: activeOcrJobs } = useActiveOcrJobs();
  const inFlight = activeOcrJobs.length > 0;

  const groups: any[] = (data as any)?.groups || [];
  if (isLoading || groups.length === 0) return null;

  // FY window as 'YYYY-MM' bounds (2026-11-03 follow-up): the month CHAIN clips
  // to the selected financial year too — statements outside it (e.g. 2026-04/05
  // while FY25-26 is selected) stay hidden with a note, instead of rendering
  // months the user did not ask about. Empty bounds (unset selector) keep the
  // full-history chain.
  const fyFrom = startDate ? startDate.slice(0, 7) : '';
  const fyTo = endDate ? endDate.slice(0, 7) : '';
  const linkMonth = (l: any) => `${l.statement_year}-${String(l.statement_month).padStart(2, '0')}`;
  const chainInFY = (g: any) => (fyFrom && fyTo
    ? g.chain.filter((l: any) => { const m = linkMonth(l); return m >= fyFrom && m <= fyTo; })
    : g.chain);
  const monthInFY = (m: string) => (!fyFrom || !fyTo) || (m >= fyFrom && m <= fyTo);

  // Visible-scoped verdict: the card must reflect what is SHOWN. A red issue
  // sitting in a hidden FY would paint the card red with nothing visible to
  // explain it — so hard issues / awaiting-review are judged on the clipped
  // chain only. ('empty_fy' = account has no statements in the selected FY.)
  const HARD_ISSUES = ['balance_mismatch', 'duplicate', 'overlap', 'date_overlap'];
  const visStatus = (g: any): string => {
    const vis = chainInFY(g);
    if (vis.length === 0) return 'empty_fy';
    if (vis.some((l: any) => l.issues.some((i: string) => HARD_ISSUES.includes(i))
      || (l.issues.includes('gap') && !l.gap_dormant))) return 'has_gaps';
    if (vis.some((l: any) => l.awaiting_review)) return 'awaiting_review';
    return 'complete';
  };
  const visStatuses = groups.map(visStatus);

  const hasIssues = visStatuses.includes('has_gaps');
  // Nov 4: needs_review members surface as amber 'awaiting review', not red —
  // they carry no verdict, only pending human confirmation.
  const hasAwaiting = visStatuses.includes('awaiting_review');
  const anyVisible = visStatuses.some((s: string) => s !== 'empty_fy');
  const dormantMonthsOf = (g: any) =>
    new Set<string>(g.chain.flatMap((l: any) => (l.gap_dormant ? (l.missing_months || []) : [])));

  const groupKey = (g: any) => type === 'card' ? (g.card_number || g.card_issuer) : g.account_number;
  const groupIcon = (g: any) => type === 'card' ? <CreditCard className="h-4 w-4 text-muted-foreground" /> : <Landmark className="h-4 w-4 text-muted-foreground" />;
  const groupLabel = (g: any) => type === 'card'
    ? `${g.card_issuer || ''} ${g.card_network || ''} ••••${g.card_number || ''}`
    : `${g.bank_name || ''} ${g.account_number || ''}`;
  const itemPeriod = (link: any) => `${link.statement_year}-${String(link.statement_month).padStart(2, '0')}`;
  const fmtAmount = (v: any) =>
    v == null ? '—' : Number(v).toLocaleString('en-HK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const issueIcon = (issues: string[]) => {
    if (issues.includes('gap')) return '🔴';
    if (issues.includes('balance_mismatch')) return '🟡';
    if (issues.includes('duplicate')) return '🟣';
    if (issues.includes('overlap') || issues.includes('date_overlap')) return '🟠';
    if (issues.includes('matched')) return '🟢';
    if (issues.includes('first')) return '⚪';
    return '⚪';
  };

  // Dormant gaps are informational (balance-neutral, 2026-10-27 ruling) — the
  // same pair the red flag path deliberately skips (reviewBlocking) must not
  // paint red here either.
  const linkIsDormantGap = (link: any) => link.issues.includes('gap') && !!link.gap_dormant;

  const groupStatusColor = (status: string) => {
    if (status === 'complete') return 'border-green-400 bg-green-50 dark:bg-green-950/30';
    if (status === 'has_gaps') return 'border-red-400 bg-red-50 dark:bg-red-950/30';
    if (status === 'has_mismatches') return 'border-yellow-400 bg-yellow-50 dark:bg-yellow-950/30';
    if (status === 'has_duplicates') return 'border-purple-400 bg-purple-50 dark:bg-purple-950/30';
    if (status === 'awaiting_review') return 'border-amber-400 bg-amber-50 dark:bg-amber-950/30';
    if (status === 'empty_fy') return 'border-border bg-muted/30';
    return 'border-orange-400 bg-orange-50 dark:bg-orange-950/30';
  };
  const groupDotColor = (status: string) =>
    status === 'complete' ? 'bg-green-500'
      : status === 'has_gaps' ? 'bg-red-500'
      : status === 'has_mismatches' ? 'bg-yellow-500'
      : status === 'awaiting_review' ? 'bg-amber-500'
      : status === 'empty_fy' ? 'bg-gray-400'
      : 'bg-orange-500';
  const groupBadge = (status: string) => {
    if (status === 'complete') {
      return <span className="text-xs font-medium px-2 py-0.5 rounded bg-green-200 text-green-800">{tr('Complete', '完整', '完整')}</span>;
    }
    if (status === 'awaiting_review') {
      return <span className="text-xs font-medium px-2 py-0.5 rounded bg-amber-200 text-amber-900">🕐 {tr('Awaiting review', '待覆核', '待复核')}</span>;
    }
    if (status === 'empty_fy') {
      return <span className="text-xs font-medium px-2 py-0.5 rounded bg-muted text-muted-foreground">{tr('No statements this year', '本年度沒有月結單', '本年度没有月结单')}</span>;
    }
    return <span className="text-xs font-medium px-2 py-0.5 rounded bg-red-200 text-red-800">{tr('Issues found', '有問題', '有问题')}</span>;
  };

  return (
    <div className={`rounded-lg border-2 p-4 space-y-3 ${
      inFlight
        ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/20'
        : hasIssues
          ? 'border-red-400 bg-red-50 dark:bg-red-950/20'
          : anyVisible
            ? 'border-green-400 bg-green-50 dark:bg-green-950/20'
            : 'border-border bg-muted/20'
    }`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between">
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          <span className="font-bold text-sm">
            {inFlight ? '⏳' : hasIssues ? '⚠️' : '✅'}{' '}
            {tr('Statement Continuity Chain', '月結單連續性檢查', '月结单连续性检查')}
          </span>
          {!expanded && <span className="text-xs text-muted-foreground ml-2">
            {groups.length} {type === 'card' ? tr('card(s)', '張卡', '张卡') : tr('account(s)', '個帳戶', '个账户')}
            {inFlight
              ? <span className="text-amber-800 dark:text-amber-200 font-medium ml-2">{tr('— checking, OCR in progress', '— 檢查中，OCR 處理中', '— 检查中，OCR 处理中')}</span>
              : hasIssues
                ? <span className="text-red-900 dark:text-red-200 font-medium ml-2">{tr('— issues found', '— 發現問題', '— 发现问题')}</span>
                : hasAwaiting
                  ? <span className="text-amber-800 dark:text-amber-200 font-medium ml-2">{tr('— awaiting review', '— 待覆核', '— 待复核')}</span>
                  : null}
          </span>}
        </div>
        <div className="flex items-center gap-1">
          {groups.map((g: any) => (
            <span key={groupKey(g)} className={`w-2.5 h-2.5 rounded-full ${groupDotColor(visStatus(g))}`} />
          ))}
        </div>
      </button>

      {expanded && (
        <div className="space-y-3 pt-1">
          {inFlight && (
            <div className="text-xs font-medium text-amber-900 dark:text-amber-200">
              {tr(
                `${activeOcrJobs.length} document(s) still scanning — the chain below only shows processed statements. Re-check once OCR completes.`,
                `${activeOcrJobs.length} 份文件仍在掃描——以下鏈條只顯示已處理的月結單，OCR 完成後請再檢查。`,
                `${activeOcrJobs.length} 份文件仍在扫描——以下链条只显示已处理的月结单，OCR 完成后请再检查。`,
              )}
            </div>
          )}
          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
            <span>🟢 {tr('Matched', '正常', '正常')}</span>
            <span>🔴 {tr('Gap', '缺失', '缺失')}</span>
            <span>🟡 {tr('Mismatch', '餘額不符', '余额不符')}</span>
            <span>🟠 {tr('Overlap', '重疊', '重叠')}</span>
            <span>🟣 {tr('Duplicate', '重複', '重复')}</span>
            <span>⚪ {tr('Dormant month (balance-neutral)', '休眠月份（餘額無變動）', '休眠月份（余额无变动）')}</span>
            <span>🕐 {tr('Awaiting review', '待覆核', '待复核')}</span>
          </div>

          {groups.map((g: any) => {
            const st = visStatus(g);
            const visChain = chainInFY(g);
            const hiddenCount = g.chain.length - visChain.length;
            return (
            <div key={groupKey(g)} className={`rounded-lg border p-3 space-y-2 ${groupStatusColor(st)}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {groupIcon(g)}
                  <span className="text-sm font-medium">{groupLabel(g)}</span>
                  {type === 'bank' && g.account_type && (
                    <span className="text-xs bg-muted px-1.5 py-0.5 rounded">{g.account_type}</span>
                  )}
                  <span className="text-xs text-muted-foreground">{g.currency}</span>
                </div>
                <span className="text-xs font-medium px-2 py-0.5 rounded">
                  {groupBadge(st)}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-1">
                {visChain.length === 0 && (
                  <span className="text-xs text-muted-foreground">
                    {tr(`No statements in FY${fyStartYear} — switch the year selector to see other years.`, `FY${fyStartYear} 沒有月結單——請切換年度選擇器查看其他年度。`, `FY${fyStartYear} 没有月结单——请切换年度选择器查看其他年度。`)}
                  </span>
                )}
                {visChain.map((link: any, idx: number) => (
                  <div key={link.id} className="flex items-center gap-1">
                    {idx > 0 && !link.issues.includes('first') && (
                      <span className="text-xs">{issueIcon(link.issues)}</span>
                    )}
                    {(() => {
                      const boxBody = (<>
                        <div className="text-center font-mono font-medium">{itemPeriod(link)}</div>
                        <div className="text-center text-[10px] font-mono text-muted-foreground" title={tr('Opening → Closing', '期初 → 期末', '期初 → 期末')}>
                          {fmtAmount(link.opening_balance)} → {fmtAmount(link.closing_balance)}
                        </div>
                        {!!link.awaiting_review && (
                          <div className="text-center text-[10px]" title={tr('Awaiting review', '待覆核', '待复核')}>🕐</div>
                        )}
                      </>);
                      const boxColor = link.issues.includes('gap') && !linkIsDormantGap(link) ? 'border-red-400 bg-red-100 dark:bg-red-900/30' :
                        link.issues.includes('balance_mismatch') ? 'border-yellow-400 bg-yellow-100 dark:bg-yellow-900/30' :
                        link.issues.includes('duplicate') ? 'border-purple-400 bg-purple-100 dark:bg-purple-900/30' :
                        link.issues.includes('overlap') || link.issues.includes('date_overlap') ? 'border-orange-400 bg-orange-100 dark:bg-orange-900/30' :
                        link.awaiting_review ? 'border-amber-400 bg-amber-100 dark:bg-amber-900/30' :
                        linkIsDormantGap(link) ? 'border-gray-300 bg-gray-100 dark:bg-gray-800/40' :
                        'border-green-300 bg-green-100 dark:bg-green-900/30';
                      if (onSelectStatement) {
                        return (
                          <button type="button" onClick={() => onSelectStatement(link.id)}
                            title={tr("Click to view this month's transactions", '點擊查看本月交易記錄', '点击查看本月交易记录')}
                            className={`relative group px-2 py-1 rounded text-xs border cursor-pointer hover:ring-2 hover:ring-primary/50 ${boxColor}`}>
                            {boxBody}
                          </button>
                        );
                      }
                      return (
                        <div className={`relative group px-2 py-1 rounded text-xs border cursor-default ${boxColor}`}>
                          {boxBody}
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>

              {/* Fiscal-period coverage (Nov 3, 2026-10-30): the chain proves
                  statements connect to each other; this proves they cover the
                  company's financial period. Dormant months the chain proved
                  balance-neutral are informational, not red. 2026-11-03:
                  scoped to the sidebar's selected financial year — periods are
                  labelled by their START year (Apr–Mar ending 2026 = FY2025),
                  which equals the selector's fyStartYear for both Apr–Mar and
                  Jan–Dec windows. Other FYs' issues collapse into one note so
                  scoping never hides them entirely. */}
              {g.coverage && (() => {
                const dormantSet = dormantMonthsOf(g);
                const allPeriods: any[] = g.coverage.periods || [];
                const selPeriods = allPeriods.filter((p: any) => p.fy === fyStartYear);
                const selMissing = selPeriods.flatMap((p: any) => (p.missing || []) as string[]);
                const hardMissing = selMissing.filter((m: string) => !dormantSet.has(m));
                const dormantMissing = selMissing.filter((m: string) => dormantSet.has(m));
                const otherHard = allPeriods
                  .filter((p: any) => p.fy !== fyStartYear)
                  .flatMap((p: any) => (p.missing || []).filter((m: string) => !dormantSet.has(m)));
                return (
                  <div className="text-xs space-y-0.5">
                    <div className="font-medium text-foreground">
                      {tr('Financial period coverage', '財務期間覆蓋', '财务期间覆盖')}
                      {selPeriods.length === 0 ? (
                        <span className="ml-1 text-muted-foreground">— {tr(`no statements in FY${fyStartYear}`, `FY${fyStartYear} 沒有月結單`, `FY${fyStartYear} 没有月结单`)}</span>
                      ) : hardMissing.length ? (
                        <span className="ml-1 text-red-900 dark:text-red-200">— {tr('statements missing', '缺少月結單', '缺少月结单')}</span>
                      ) : (
                        <span className="ml-1 text-green-800 dark:text-green-300">— {tr('covered', '已覆蓋', '已覆盖')}</span>
                      )}
                    </div>
                    {selPeriods.map((p: any) => {
                      const pHard = (p.missing || []).filter((m: string) => !dormantSet.has(m));
                      const pDormant = (p.missing || []).filter((m: string) => dormantSet.has(m));
                      return (
                        <div key={p.fy} className={pHard.length ? 'text-red-900 dark:text-red-200' : 'text-muted-foreground'}>
                          FY{p.fy}: {p.present}/{p.expected}
                          {!!pHard.length && ` — ${tr('missing', '缺少', '缺少')}: ${pHard.join(', ')}`}
                          {!!pDormant.length && (
                            <span className="text-muted-foreground">
                              {' '}(⚪ {tr('dormant', '休眠', '休眠')}: {pDormant.join(', ')})
                            </span>
                          )}
                          {!!p.soft_missing?.length && (
                            <span className="text-muted-foreground">
                              {' '}({tr('before first statement:', '首份月結單之前：', '首份月结单之前：')} {p.soft_missing.join(', ')})
                            </span>
                          )}
                        </div>
                      );
                    })}
                    {!!dormantMissing.length && (
                      <div className="text-muted-foreground">
                        ⚪ {tr('Dormant months (balance-neutral, per the chain above)', '休眠月份（餘額無變動，見上方鏈條）', '休眠月份（余额无变动，见上方链条）')}: {dormantMissing.join(', ')}
                      </div>
                    )}
                    {!!otherHard.length && (
                      <div className="text-muted-foreground">
                        {tr(`${otherHard.length} missing month(s) in other financial years — switch the year selector above to review.`, `其他財政年度尚有 ${otherHard.length} 個缺失月份——請切換上方年度選擇器查看。`, `其他财政年度尚有 ${otherHard.length} 个缺失月份——请切换上方年度选择器查看。`)}
                      </div>
                    )}
                  </div>
                );
              })()}
              {!g.coverage && g.chain.some((l: any) => l.missing_months?.length) && (() => {
                const dormantSet = dormantMonthsOf(g);
                const all = g.chain.flatMap((l: any) => l.missing_months || []).filter(monthInFY);
                const hard = all.filter((m: string) => !dormantSet.has(m));
                const dormant = all.filter((m: string) => dormantSet.has(m));
                return (
                  <>
                    {!!hard.length && (
                      <div className="text-xs text-red-900 dark:text-red-200">
                        {tr('Missing months', '缺少月份', '缺少月份')}: {hard.join(', ')}
                      </div>
                    )}
                    {!!dormant.length && (
                      <div className="text-xs text-muted-foreground">
                        ⚪ {tr('Dormant months (balance-neutral)', '休眠月份（餘額無變動）', '休眠月份（余额无变动）')}: {dormant.join(', ')}
                      </div>
                    )}
                  </>
                );
              })()}

              {visChain.some((l: any) => l.issues.includes('balance_mismatch')) && (
                <div className="text-xs text-yellow-900 dark:text-yellow-200 mt-2 space-y-1">
                  {tr('Balance mismatches between closing and next opening:', '期末與下期期初餘額不符：', '期末与下期期初余额不符：')}
                  {visChain.filter((l: any) => l.issues.includes('balance_mismatch')).map((l: any) => {
                    const idx = visChain.findIndex((c: any) => c.id === l.id);
                    const prev = idx > 0 ? visChain[idx - 1] : null;
                    return (
                      <div key={l.id} className="font-mono">
                        {prev ? `${itemPeriod(prev)} ${tr('closing', '期末', '期末')} ${fmtAmount(prev.closing_balance)} → ${itemPeriod(l)} ${tr('opening', '期初', '期初')} ${fmtAmount(l.actual_opening)}` : ''}
                        {' '}· {tr('expected', '應為', '应为')} {fmtAmount(l.expected_opening)}
                      </div>
                    );
                  })}
                </div>
              )}

              {hiddenCount > 0 && (
                <div className="text-xs text-muted-foreground">
                  {tr(`${hiddenCount} statement(s) outside FY${fyStartYear} hidden — switch the year selector to see other years.`, `已隱藏 FY${fyStartYear} 以外的 ${hiddenCount} 份月結單——請切換年度選擇器查看其他年度。`, `已隐藏 FY${fyStartYear} 以外的 ${hiddenCount} 份月结单——请切换年度选择器查看其他年度。`)}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
